import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  RotateCcw, ArrowLeft, Trophy, History, Heart, Timer, RefreshCw
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MAX_BONUS = 500;

// --- Pure Matrix Utilities ---

const createGrid = (size) => Array(size).fill().map(() => Array(size).fill(0));

const cloneGrid = (grid) => grid.map(row => [...row]);

const areGridsEqual = (g1, g2) => JSON.stringify(g1) === JSON.stringify(g2);

const rotateGrid = (grid) => {
  const N = grid.length;
  // Transpose and reverse rows (Rotate 90deg clockwise)
  return grid[0].map((val, index) => grid.map(row => row[index]).reverse());
};

const mirrorGrid = (grid) => {
  // Horizontal Mirror (Reverse rows)
  return grid.map(row => [...row].reverse());
};

// Check if g1 is ANY rotation of g2
const isRotationOf = (g1, g2) => {
  let curr = g2;
  for (let i = 0; i < 4; i++) {
    if (areGridsEqual(g1, curr)) return true;
    curr = rotateGrid(curr);
  }
  return false;
};

// --- Strict Validation Checks ---

const isAsymmetrical = (grid) => {
  // 1. Check Rotational Symmetry
  let curr = rotateGrid(grid); // 90
  if (areGridsEqual(grid, curr)) return false;
  
  curr = rotateGrid(curr); // 180
  if (areGridsEqual(grid, curr)) return false;
  
  curr = rotateGrid(curr); // 270
  if (areGridsEqual(grid, curr)) return false;

  // 2. Check Reflectional Symmetry (Chirality)
  const mirror = mirrorGrid(grid);
  
  // Is mirror same as original?
  if (areGridsEqual(grid, mirror)) return false;
  
  // Is mirror same as ANY rotation of original?
  // (If yes, then target is effectively Achiral visually under rotation)
  if (isRotationOf(grid, mirror)) return false;

  return true;
};

// Generate a valid asymmetrical target
const generateTarget = (size, filledCount) => {
  let attempts = 0;
  let bestFallback = null;

  while (attempts < 500) {
    attempts++;
    const grid = createGrid(size);
    let count = 0;
    
    // Fill random cells
    while (count < filledCount) {
      const r = Math.floor(Math.random() * size);
      const c = Math.floor(Math.random() * size);
      if (grid[r][c] === 0) {
        grid[r][c] = 1;
        count++;
      }
    }
    
    // Keep first generated as fallback to avoid crashing
    if (!bestFallback) bestFallback = grid;

    // Strict Asymmetry Check
    if (isAsymmetrical(grid)) {
      return grid;
    }
  }
  return bestFallback || createGrid(size); 
};

const alterGrid = (grid) => {
  // Deep copy
  let newGrid = cloneGrid(grid);
  const size = grid.length;
  
  // Find filled and empty
  const filled = [];
  const empty = [];
  grid.forEach((row, r) => row.forEach((val, c) => {
    if (val) filled.push([r,c]);
    else empty.push([r,c]);
  }));

  // Try to create an alteration that isn't just a rotation of original
  // Limit retries
  let attempts = 0;
  while (attempts < 20) {
    if (filled.length > 0 && empty.length > 0) {
        const [remR, remC] = filled[Math.floor(Math.random() * filled.length)];
        const [addR, addC] = empty[Math.floor(Math.random() * empty.length)];
        
        // Apply change
        newGrid[remR][remC] = 0;
        newGrid[addR][addC] = 1;
        
        // Ensure newGrid is NOT a valid rotation of original grid
        // Also ensure newGrid is NOT equal to original grid
        if (!isRotationOf(newGrid, grid)) {
            return newGrid;
        } else {
            // Revert and try again
            newGrid[remR][remC] = 1;
            newGrid[addR][addC] = 0;
        }
    }
    attempts++;
  }
  
  return newGrid;
};

// --- Main Component ---

export default function SpatialRotationPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();

  // --- State ---
  const [gameState, setGameState] = useState('intro'); // intro, playing, feedback, gameover
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(0);

  // Round Data
  const [target, setTarget] = useState(createGrid(3));
  const [options, setOptions] = useState([]); // { id, grid, type, status }
  const [gridSize, setGridSize] = useState(3);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Stats
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);

  // Refs
  const timerRef = useRef(null);

  // --- Effects ---

  useEffect(() => {
    window.scrollTo(0, 0);
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    fetchStats();
    return () => clearInterval(timerRef.current);
  }, []);

  // --- API ---

  const fetchStats = async () => {
    try {
      const [lbRes, attRes] = await Promise.all([
        axios.get(`${API}/spatial-rotation/leaderboard`),
        localStorage.getItem('token') ? axios.get(`${API}/spatial-rotation/results`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }) : Promise.resolve({ data: [] })
      ]);
      setLeaderboard(lbRes.data);
      setAttempts(attRes.data);
    } catch (e) {
      console.error("Stats fetch error", e);
    }
  };

  // --- Game Logic ---

  const getLevelConfig = (lvl) => {
    if (lvl <= 3) return { size: 3, filled: 4, time: 8 };
    if (lvl <= 7) return { size: 4, filled: 6, time: 6 };
    if (lvl <= 12) return { size: 5, filled: 9, time: 5 };
    if (lvl <= 20) return { size: 6, filled: 12, time: 4 };
    return { size: 7, filled: 15, time: 3 };
  };

  const startGame = () => {
    setScore(0);
    setLives(3);
    setLevel(1);
    startLevel(1);
  };

  const startLevel = (lvl) => {
    const config = getLevelConfig(lvl);
    setGridSize(config.size);
    setMaxTime(config.time * 1000);
    setTimeLeft(config.time * 1000);
    setSelectedIdx(null);

    // 1. Generate Strictly Asymmetrical Target
    const newTarget = generateTarget(config.size, config.filled);
    setTarget(newTarget);

    // 2. Generate Distinct Options Loop
    let correct, trap, flaw;
    let validOptions = false;
    let optionAttempts = 0;

    while (!validOptions && optionAttempts < 50) {
        optionAttempts++;

        // A: Correct (Rotated 90, 180, or 270)
        // Ensure it's not 0 deg (identical to target) for challenge, unless forced
        correct = cloneGrid(newTarget);
        const rotC = Math.floor(Math.random() * 3) + 1; // 1, 2, or 3 rotations
        for(let i=0; i<rotC; i++) correct = rotateGrid(correct);

        // B: Trap (Mirrored then rotated)
        trap = mirrorGrid(newTarget);
        const rotT = Math.floor(Math.random() * 4);
        for(let i=0; i<rotT; i++) trap = rotateGrid(trap);

        // C: Flaw (Rotated but altered tile)
        // Start from a rotation, then alter
        let baseFlaw = cloneGrid(newTarget);
        const rotF = Math.floor(Math.random() * 4);
        for(let i=0; i<rotF; i++) baseFlaw = rotateGrid(baseFlaw);
        flaw = alterGrid(baseFlaw);

        // --- Collision Detection ---
        
        // 1. Are any options identical to each other?
        if (areGridsEqual(correct, trap)) continue;
        if (areGridsEqual(correct, flaw)) continue;
        if (areGridsEqual(trap, flaw)) continue;

        // 2. Safety: Is Trap actually a valid rotation of Target? (Should be covered by isAsymmetrical, but double check)
        if (isRotationOf(trap, newTarget)) continue;

        // 3. Safety: Is Flaw actually a valid rotation of Target?
        if (isRotationOf(flaw, newTarget)) continue;

        validOptions = true;
    }

    const newOptions = [
      { id: 'A', grid: correct, type: 'correct', status: 'idle' },
      { id: 'B', grid: trap, type: 'trap', status: 'idle' },
      { id: 'C', grid: flaw, type: 'flaw', status: 'idle' }
    ].sort(() => Math.random() - 0.5);

    setOptions(newOptions);
    setGameState('playing');

    // Start Timer
    if (timerRef.current) clearInterval(timerRef.current);
    const startTime = Date.now();
    const duration = config.time * 1000;
    
    timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, duration - elapsed);
        setTimeLeft(remaining);
        
        if (remaining <= 0) {
            clearInterval(timerRef.current);
            handleTimeout();
        }
    }, 50);
  };

  const handleInput = (option, idx) => {
    // Clear timer immediately
    if (timerRef.current) clearInterval(timerRef.current);
    
    // Set visual selection state
    setSelectedIdx(idx);
    
    const isCorrect = option.type === 'correct';

    // Update Option Visuals
    const updatedOptions = options.map((opt, i) => {
      // Logic for feedback phase:
      // If this is the selected option -> mark correct/wrong
      // If this is NOT selected but IS correct -> mark correct (reveal)
      if (i === idx) return { ...opt, status: isCorrect ? 'correct' : 'wrong' };
      if (opt.type === 'correct') return { ...opt, status: 'correct' }; 
      return { ...opt, status: 'dimmed' };
    });
    setOptions(updatedOptions);

    if (isCorrect) {
      // Score
      const bonus = Math.floor((timeLeft / maxTime) * MAX_BONUS);
      const roundScore = (level * 100) + bonus;
      setScore(s => s + roundScore);
      // toast.success(`Correct! +${roundScore}`);
      
      setGameState('feedback');
      setTimeout(() => {
        setLevel(l => l + 1);
        startLevel(level + 1);
      }, 500);
    } else {
      // Fail
      handleLifeLoss("Incorrect match!");
    }
  };

  const handleTimeout = () => {
    // Reveal answer
    const updatedOptions = options.map(opt => ({
      ...opt,
      status: opt.type === 'correct' ? 'correct' : 'dimmed'
    }));
    setOptions(updatedOptions);
    handleLifeLoss("Time's Up!");
  };

  const handleLifeLoss = (reason) => {
    const newLives = lives - 1;
    setLives(newLives);
    toast.error(reason);

    setGameState('feedback');
    
    if (newLives <= 0) {
      setTimeout(endGame, 1000);
    } else {
      setTimeout(() => {
        // Retry level (new pattern)
        startLevel(level);
      }, 1500);
    }
  };

  const endGame = async () => {
    setGameState('gameover');
    if (user) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/spatial-rotation/results`, {
          score: score,
          level_reached: level
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchStats();
      } catch (e) { console.error(e); }
    }
  };

  // --- Components ---

  const GridDisplay = ({ grid, size, className }) => (
    <div 
      className={`grid gap-1 bg-slate-800 p-1.5 rounded-lg border border-slate-700 ${className}`}
      style={{ 
        gridTemplateColumns: `repeat(${size}, 1fr)`,
        aspectRatio: '1/1'
      }}
    >
      {grid && grid.length > 0 && grid.flat().map((cell, i) => (
        <div 
          key={i} 
          className={`rounded-sm transition-colors duration-200 ${
            cell ? 'bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.6)]' : 'bg-slate-900/40'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans selection:bg-cyan-500/30">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 p-4 flex justify-between items-center bg-[#020617]/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" /> Exit
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
                {user?.username}
            </div>
        </div>
        <div className="flex gap-6 font-mono text-sm">
           <div className="flex items-center gap-2">
             <Trophy className="w-4 h-4 text-yellow-500" />
             <span className="text-xl font-bold">{score}</span>
           </div>
           <div className="flex items-center gap-2 text-red-500">
             <Heart className="w-4 h-4 fill-current" />
             <span className="text-xl font-bold">{lives}</span>
           </div>
        </div>
      </header>

      {/* Main */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Column */}
            <div className="lg:col-span-2">
              <div className="flex flex-col items-center justify-center p-6 relative w-full bg-slate-900/30 rounded-3xl border border-slate-800/50 min-h-[600px]">
                
                {/* Intro Overlay */}
                {gameState === 'intro' && (
                  <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500">
                    <div className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-cyan-500/20">
                      <RotateCcw className="w-12 h-12 text-white" />
                    </div>
                    <div>
                      <h1 className="text-4xl font-black tracking-tight mb-4">SPATIAL ROTATION</h1>
                      <p className="text-slate-400 text-lg">Mental matrix manipulation.<br/>Find the match.</p>
                    </div>
                    <Button onClick={startGame} className="w-full py-8 text-xl font-bold bg-white text-black hover:bg-slate-200 rounded-xl">Start Test</Button>
                  </div>
                )}

                {/* Game Over Overlay */}
                {gameState === 'gameover' && (
                  <div className="text-center space-y-8 animate-in zoom-in duration-500">
                    <div className="text-6xl mb-4">💀</div>
                    <h2 className="text-4xl font-black">GAME OVER</h2>
                    <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 w-full max-w-xs mx-auto">
                      <div className="text-slate-500 text-sm uppercase mb-1">Final Score</div>
                      <div className="text-5xl font-mono font-bold text-white">{score}</div>
                    </div>
                    <Button onClick={startGame} className="w-full py-6 text-lg font-bold bg-white text-black hover:bg-slate-200">
                      <RefreshCw className="w-5 h-5 mr-2" /> Play Again
                    </Button>
                  </div>
                )}

                {/* Active Game */}
                {(gameState === 'playing' || gameState === 'feedback') && (
                  <div className="w-full max-w-2xl space-y-8 animate-in fade-in">
                    {/* HUD */}
                    <div className="flex justify-between items-center px-4">
                      <div className="text-cyan-400 font-mono text-sm uppercase tracking-widest">Level {level}</div>
                      <div className="flex items-center gap-2 text-slate-400 font-mono text-sm">
                        <Timer className="w-4 h-4" /> {(timeLeft / 1000).toFixed(1)}s
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500 transition-all duration-75 linear" style={{ width: `${(timeLeft / maxTime) * 100}%` }} />
                    </div>

                    {/* Target */}
                    <div className="flex justify-center py-4">
                      <div className="relative">
                        <div className="absolute -top-6 left-0 w-full text-center text-xs font-bold text-slate-500 uppercase tracking-widest">Target</div>
                        <div className="w-48 h-48 sm:w-56 sm:h-56">
                          <GridDisplay grid={target} size={gridSize} />
                        </div>
                      </div>
                    </div>

                    {/* Options */}
                    <div className="grid grid-cols-3 gap-4 sm:gap-6">
                      {options.map((opt, idx) => {
                        const isSelected = selectedIdx === idx;
                        const isFeedback = gameState === 'feedback';
                        const isCorrect = opt.type === 'correct';
                        
                        let borderClass = 'border-slate-700';
                        let bgClass = 'bg-slate-800/30';
                        
                        if (isFeedback) {
                            if (isCorrect) {
                                borderClass = 'border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.4)]';
                                bgClass = 'bg-emerald-500/10';
                            } else if (isSelected) {
                                borderClass = 'border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.4)]';
                                bgClass = 'bg-red-500/10';
                            } else {
                                borderClass = 'border-slate-800 opacity-50'; // Dim irrelevant
                            }
                        } else {
                            borderClass = 'border-slate-700 hover:border-cyan-500';
                            bgClass = 'bg-slate-800/30 hover:bg-slate-800';
                        }

                        return (
                          <button
                            key={idx}
                            onClick={() => handleInput(opt, idx)}
                            disabled={gameState !== 'playing'}
                            className={`relative p-3 rounded-xl border-2 transition-all duration-200 active:scale-95 flex flex-col items-center focus:outline-none focus:ring-0 ${borderClass} ${bgClass}`}
                          >
                            <GridDisplay grid={opt.grid} size={gridSize} className="w-full pointer-events-none" />
                            <div className={`mt-3 text-xs font-bold ${isFeedback && isCorrect ? 'text-emerald-400' : 'text-slate-500'}`}>
                                {String.fromCharCode(65 + idx)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Column */}
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.map((entry, i) => (
                    <div key={i} className="flex justify-between items-center p-3 mb-2 rounded-lg bg-slate-800/50">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i < 3 ? 'bg-yellow-500 text-black' : 'bg-slate-700'}`}>{i+1}</span>
                        <span className="font-medium text-sm">{entry.username}</span>
                      </div>
                      <span className="font-bold text-cyan-400">{entry.score}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-heading font-bold text-lg">History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.map((att, i) => (
                    <div key={i} className="flex justify-between items-center p-3 mb-2 rounded-lg bg-slate-800/50">
                      <span className="text-slate-400 text-xs">{new Date(att.created_at).toLocaleDateString()}</span>
                      <div className="text-right">
                        <div className="font-bold text-cyan-400">{att.score}</div>
                        <div className="text-xs text-slate-500">Lvl {att.level_reached}</div>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
